import {
  formatDateToDutch,
  DataTable,
  createActionsColumn,
  createCustomColumn,
  selectColumn,
  createSortableColumn,
  Badge,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import ScreenWrapper from "@/components/ScreenWrapper"
import { TABLE_PAGE_SIZE } from "toptaal-shared"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import { Lesson } from "@/api/types/lessons"
import EditLessonModal from "@/components/modals/planning/EditLessonModal"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { lessonQueryKeys } from "@/api/query-keys/lessons"
import { can } from "@/utils/can"
import {
  getLessonStatusLabel,
  lessonStatusToBadgeVariant,
} from "@/utils/lessonStatus"
import CancelLessonModal from "@/components/modals/lessons/CancelLessonModal"
import RemoveTeacherModal from "@/components/modals/lessons/RemoveTeacherModal"
import RemoveLocationModal from "@/components/modals/lessons/RemoveLocatationModal"
import {
  LESSON_STORAGE_KEY,
  useLessonFilters,
} from "@/hooks/filters/useLessonFilters"

export const Route = createFileRoute("/beheer/lessen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "planning"),
  validateSearch: createTableSearchSchema(),
})

const ALL_COLUMN_IDS = [
  "id",
  "lessonGroupName",
  "locationName",
  "classroomName",
  "teacherName",
  "date",
  "status",
]

function RouteComponent() {
  const router = useRouter()
  const [editInfoOpen, setEditInfoOpen] = useState(false)
  const [cancelLessonOpen, setCancelLessonOpen] = useState(false)
  const [removeTeacherOpen, setRemoveTeacherOpen] = useState(false)
  const [removeLocationOpen, setRemoveLocationOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Lesson | null>(null)
  const searchParams = useSearch({ from: "/beheer/lessen/" })
  const navigate = useNavigate({ from: "/beheer/lessen/" })

  const { filterItems, activeFilters, queryFn } = useLessonFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...lessonQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    allColumnIds: ALL_COLUMN_IDS,
    storageKey: LESSON_STORAGE_KEY,
  })

  const closeDialog = () => {
    setEditInfoOpen(false)
    setActiveRow(null)
  }

  const lessons = data?.data || []

  const isLessonInPast = (lesson: Lesson) => {
    const today = new Date()
    const lessonDate = new Date(lesson.date)

    return lessonDate < today
  }

  const handleNavigateDetail = (lessonId: Lesson["id"]) => {
    if (can("read", "calendar")) {
      return router.navigate({
        to: "/agenda/les/$lessonId",
        search: { from: router.state.location.href },
        params: {
          lessonId: lessonId.toString(),
        },
      })
    }
  }

  const columns: ColumnDef<Lesson>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({
      accessorKey: "lessonGroupName",
      name: "Groepsnaam",
    }),
    createSortableColumn({
      accessorKey: "locationName",
      name: "Leslocatie",
    }),
    createSortableColumn({
      accessorKey: "classroomName",
      name: "Lokaal",
    }),
    createSortableColumn({
      accessorKey: "teacherName",
      name: "Docent",
    }),
    createCustomColumn({
      id: "date",
      name: "Lesdag en tijd",
      cell: ({ row }) => (
        <p>
          {formatDateToDutch(row.original.date, { format: "dateWithWeekday" })}{" "}
          {" - "}
          {row.original.startTime}
        </p>
      ),
      accessorKey: "date",
    }),
    createCustomColumn({
      id: "status",
      name: "Status",
      cell: ({ row }) => (
        <Badge variant={lessonStatusToBadgeVariant(row.original.status)}>
          {getLessonStatusLabel(row.original.status)}
        </Badge>
      ),
      accessorKey: "status",
    }),
    createActionsColumn([
      {
        label: "Les wijzigen",
        onClick: (e, lesson) => {
          e.stopPropagation()
          setActiveRow(lesson)
          setEditInfoOpen(true)
        },
        hidden: (lesson) =>
          lesson.status === "cancelled" || isLessonInPast(lesson),
      },
      {
        label: "Laten vervallen",
        onClick: (e, lesson) => {
          e.stopPropagation()
          setActiveRow(lesson)
          setCancelLessonOpen(true)
        },
        hidden: (lesson) =>
          lesson.status === "cancelled" || isLessonInPast(lesson),
      },
      {
        label: "Docent verwijderen",
        onClick: (e, lesson) => {
          e.stopPropagation()
          setActiveRow(lesson)
          setRemoveTeacherOpen(true)
        },
        hidden: (lesson) =>
          !lesson.teacherId ||
          lesson.status === "cancelled" ||
          isLessonInPast(lesson),
      },
      {
        label: "Locatie verwijderen",
        onClick: (e, lesson) => {
          e.stopPropagation()
          setActiveRow(lesson)
          setRemoveLocationOpen(true)
        },
        hidden: (lesson) =>
          !lesson.locationId ||
          lesson.status === "cancelled" ||
          isLessonInPast(lesson),
      },
    ]),
  ]

  const { mutate: exportLessons } = useExportMutation({
    exportApiPrefix: "lessons",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Lessen"
        searchPlaceholder="Filter lessen..."
        data={lessons}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportLessons,
            }}
            createAction={{
              label: "Nieuwe les",
              to: "/beheer/lessen/nieuwe-les",
              entity: "planning",
              permissions: "create",
            }}
          />
        }
      />

      {editInfoOpen && activeRow && (
        <EditLessonModal
          editInfoOpen={editInfoOpen}
          setEditInfoOpen={setEditInfoOpen}
          activeRow={activeRow}
          closeDialog={closeDialog}
        />
      )}

      {cancelLessonOpen && activeRow && (
        <CancelLessonModal
          isOpen={cancelLessonOpen}
          setIsOpen={setCancelLessonOpen}
          lessonId={activeRow.id}
        />
      )}

      {removeTeacherOpen && activeRow && (
        <RemoveTeacherModal
          isOpen={removeTeacherOpen}
          setIsOpen={setRemoveTeacherOpen}
          lessonId={activeRow.id}
        />
      )}
      {removeLocationOpen && activeRow && (
        <RemoveLocationModal
          isOpen={removeLocationOpen}
          setIsOpen={setRemoveLocationOpen}
          lessonId={activeRow.id}
        />
      )}
    </ScreenWrapper>
  )
}
